import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Combobox, MenuGroup, SelectItem, TextField } from '@nexds/web'
import * as zod from 'zod'

import { InjectionTokens } from '@/controller/tokens'

import { FuelData, FuelLayer } from '@/domain/protocols/Fuel'
import { AuthState, AuthStateProps } from '@/domain/states/AuthState'

import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'
import { IFuelRepository } from '@/data/FuelRepository/IFuelRepository'

import {
  ActionsRow,
  AddContactButton,
  ContactGroup,
  ContactHeader,
  ContactList,
  ContactRow,
  ContactTitle,
  FieldGroup,
  FormContainer,
  FuelTypeGroup
} from './RegisterProviderForm.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import {
  validateContactPhones,
  validateCurrencyField,
  validateRequiredField
} from '@/presentation/modules/Base/components/FuelForms/utils/fuelFormValidation'
import { FuelSelectablePriceField } from '@/presentation/modules/Base/components/FuelSelectablePriceField'
import { sendMetrics } from '@/presentation/utils/sendMetrics'
import { EMPTY_CURRENCY_VALUE, formatCurrencyFromDigits, parseCurrencyInputToNumber } from '@/utils/currency'
import { formatInUTC } from '@/utils/date'
import { GlobalToastUtil } from '@/utils/globalToastUtil'
import { formatPhoneNumber } from '@/utils/string'

interface RegisterProviderFormProps {
  source: 'fuel-tool' | 'fuel-feature-info'
  mode?: 'register' | 'edit'
  adhpCode?: string
  initialProvider?: string
  initialLayer?: FuelLayer
  onCancel: () => void
  onSuccess?: () => void
}

interface AdhpOption {
  label: string
  value: string
}

const ADHP_SEARCH_MIN_CHARS = 1
const ADHP_SEARCH_MAX_RESULTS = 20
const ADHP_SEARCH_DEBOUNCE_MS = 200

type TranslationFn = ReturnType<typeof useI18n>['t']

const buildRegisterProviderSchema = (t: TranslationFn) =>
  zod
    .object({
      adhpCode: zod.string().trim(),
      provider: zod.string().trim(),
      avgasProvider: zod.boolean(),
      avgasPrice: zod.string().trim(),
      jetProvider: zod.boolean(),
      jetPrice: zod.string().trim(),
      contacts: zod.array(
        zod.object({
          phone: zod.string().trim(),
          responsible: zod.string().trim()
        })
      ),
      notes: zod.string().trim()
    })
    .superRefine((data, ctx) => {
      validateRequiredField({
        ctx,
        value: data.adhpCode,
        path: ['adhpCode'],
        message: t('FUEL_MODAL_REGISTER-PROVIDER_ERROR_ADHP_REQUIRED')
      })

      validateRequiredField({
        ctx,
        value: data.provider,
        path: ['provider'],
        message: t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_PROVIDER_PLACEHOLDER')
      })

      if (data.avgasProvider && data.avgasPrice) {
        validateCurrencyField({
          ctx,
          value: data.avgasPrice,
          path: ['avgasPrice'],
          requiredMessage: t('FUEL_MODAL_REGISTER-PRICE_ERROR_PRICE_REQUIRED'),
          invalidMessage: t('FUEL_MODAL_REGISTER-PRICE_ERROR_PRICE_POSITIVE')
        })
      }

      if (data.jetProvider && data.jetPrice) {
        validateCurrencyField({
          ctx,
          value: data.jetPrice,
          path: ['jetPrice'],
          requiredMessage: t('FUEL_MODAL_REGISTER-PRICE_ERROR_PRICE_REQUIRED'),
          invalidMessage: t('FUEL_MODAL_REGISTER-PRICE_ERROR_PRICE_POSITIVE')
        })
      }

      validateContactPhones({
        ctx,
        contacts: data.contacts,
        requiredMessage: t('FUEL_MODAL_REGISTER-PROVIDER_ERROR_PHONE_REQUIRED'),
        invalidMessage: t('FUEL_MODAL_REGISTER-PROVIDER_ERROR_PHONE_INVALID')
      })
    })

function RegisterProviderForm({
  source,
  mode = 'register',
  adhpCode,
  initialProvider,
  initialLayer,
  onCancel,
  onSuccess
}: RegisterProviderFormProps) {
  const fuelRepository = useInjection<IFuelRepository>(InjectionTokens.FuelRepository)
  const aeroInfoRepository = useInjection<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository)
  const { t } = useI18n()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [adhpFieldValue, setAdhpFieldValue] = useState('')
  const [adhpOptions, setAdhpOptions] = useState<AdhpOption[]>([])
  const [authState] = useGlobalState<AuthState, AuthStateProps>(InjectionTokens.AuthState)
  const isFeatureInfoMode = source === 'fuel-feature-info'
  const isEditMode = mode === 'edit'

  const registerProviderSchema = useMemo(() => buildRegisterProviderSchema(t), [t])

  type RegisterProviderFormData = zod.infer<typeof registerProviderSchema>

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<RegisterProviderFormData>({
    resolver: zodResolver(registerProviderSchema),
    defaultValues: {
      adhpCode: adhpCode ?? '',
      provider: initialProvider ?? '',
      avgasProvider: isFeatureInfoMode ? initialLayer === 'avgas' : true,
      avgasPrice: '',
      jetProvider: isFeatureInfoMode ? initialLayer === 'jet-a' : true,
      jetPrice: '',
      contacts: [{ phone: '', responsible: '' }],
      notes: ''
    }
  })

  const { fields: contactFields, append } = useFieldArray({
    control,
    name: 'contacts'
  })

  const avgasProvider = watch('avgasProvider')
  const avgasPrice = watch('avgasPrice')
  const jetProvider = watch('jetProvider')
  const jetPrice = watch('jetPrice')

  useEffect(() => {
    if (!adhpCode) return
    setAdhpFieldValue(adhpCode)
    setValue('adhpCode', adhpCode)
  }, [adhpCode, setValue])

  useEffect(() => {
    if (isFeatureInfoMode) {
      setAdhpOptions([])
      return
    }

    const normalizedQuery = adhpFieldValue.trim()
    if (normalizedQuery.length < ADHP_SEARCH_MIN_CHARS) {
      setAdhpOptions([])
      return
    }

    let isActive = true
    const timeoutId = window.setTimeout(async () => {
      const results = await aeroInfoRepository.searchByInfo(normalizedQuery, ADHP_SEARCH_MAX_RESULTS, true)
      if (!isActive) return

      const nextOptions = results
        .filter((waypoint) => waypoint.waypointType?.codeName === 'ADHP' && waypoint.code)
        .map((waypoint) => ({
          value: waypoint.code,
          label: `${waypoint.code} - ${waypoint.name}`
        }))

      setAdhpOptions(nextOptions)
    }, ADHP_SEARCH_DEBOUNCE_MS)

    return () => {
      isActive = false
      window.clearTimeout(timeoutId)
    }
  }, [adhpFieldValue, aeroInfoRepository, isFeatureInfoMode])

  useEffect(() => {
    if (!initialProvider) return
    setValue('provider', initialProvider)
  }, [initialProvider, setValue])

  const onSubmit = async (formData: RegisterProviderFormData) => {
    const contactName =
      [authState.user.profile?.name, authState.user.profile?.surname].filter(Boolean).join(' ') || 'Nome indisponível'
    const contactWhatsapp =
      [authState.user.profile?.phoneDDD, authState.user.profile?.phoneNumber]
        .filter((v) => v != null && v !== '' && v !== 'null')
        .join(' ') || authState.user.email
    const submittedAt = formatInUTC(new Date(), 'yyyy-MM-dd') ?? new Date().toISOString().split('T')[0]
    const parsedAvgasPrice = formData.avgasProvider ? parseCurrencyInputToNumber(formData.avgasPrice) : null
    const parsedJetPrice = formData.jetProvider ? parseCurrencyInputToNumber(formData.jetPrice) : null

    const payload: FuelData = {
      adhpCode: formData.adhpCode.trim().toUpperCase(),
      provider: formData.provider.trim(),
      avgasProvider: formData.avgasProvider,
      jetProvider: formData.jetProvider,
      avgasPrice: formData.avgasProvider && parsedAvgasPrice !== null ? parsedAvgasPrice : undefined,
      lastAvgasUpdated: formData.avgasProvider ? submittedAt : undefined,
      jetPrice: formData.jetProvider && parsedJetPrice !== null ? parsedJetPrice : undefined,
      lastJetUpdated: formData.jetProvider ? submittedAt : undefined,
      phone: formData.contacts
        .map((contactItem) => {
          // Normaliza para apenas dígitos e reformata, descartando o placeholder "_" da máscara
          // e corrigindo o agrupamento de telefones fixos (10 dígitos).
          const digits = contactItem.phone.replace(/\D/g, '')
          const phone = digits ? formatPhoneNumber(digits) : ''
          const responsible = contactItem.responsible.trim()
          if (!phone) return null
          return responsible ? `${phone} ${responsible}` : phone
        })
        .filter((phone): phone is string => Boolean(phone)),
      contactName: contactName || undefined,
      contactWhatsapp: contactWhatsapp || undefined,
      notes: formData.notes || undefined
    }

    setIsSubmitting(true)
    const result = await fuelRepository.registerProvider(payload)

    // `mode` distingue cadastro de edição: os dois compartilham este mesmo submit.
    const submitMetricProps = {
      adhpCode: payload.adhpCode,
      mode,
      origin: source,
      hasAvgas: payload.avgasProvider,
      hasJet: payload.jetProvider
    }

    if (result.isFailure) {
      sendMetrics(MetricsEvents.FUEL_PROVIDER_FAILED, submitMetricProps)
      GlobalToastUtil.send({
        label: t('FUEL_MODAL_REGISTER-PROVIDER_TOAST_ERROR'),
        variant: 'error',
        dismissible: true
      })
      setIsSubmitting(false)
      return
    }

    sendMetrics(MetricsEvents.FUEL_PROVIDER_SAVED, submitMetricProps)

    GlobalToastUtil.send({
      label: t('FUEL_MODAL_REGISTER-PROVIDER_TOAST_SUCCESS'),
      variant: 'primary',
      dismissible: true
    })
    setIsSubmitting(false)
    onSuccess?.()
  }

  return (
    <FormContainer noValidate onSubmit={handleSubmit(onSubmit)}>
      <FieldGroup>
        <Combobox
          label={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_ADHP_LABEL')}
          value={adhpFieldValue}
          onChange={(event) => {
            const typedValue = event.target.value
            setAdhpFieldValue(typedValue)
            const inferredCode = typedValue.split(' - ')[0]?.trim().toUpperCase()
            setValue('adhpCode', inferredCode || '')
          }}
          onSelectItem={(selectedValue) => {
            const selected = adhpOptions.find((item) => item.value === selectedValue)
            setAdhpFieldValue(selected?.label ?? selectedValue)
            setValue('adhpCode', selectedValue)
          }}
          size="md"
          placeholder={t('WAYPOINT_TYPE_ADHP')}
          helpGutter={!!errors.adhpCode}
          helpMessage={errors.adhpCode?.message}
          disabled={isFeatureInfoMode}
        >
          {adhpOptions.map((option) => (
            <SelectItem key={option.value} value={option.value} label={option.label} />
          ))}
        </Combobox>
      </FieldGroup>

      <FieldGroup>
        <TextField
          label={t('FUEL_MODAL_REGISTER-PRICE_FIELD_PROVIDER_LABEL')}
          size="md"
          placeholder={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_PROVIDER_PLACEHOLDER')}
          helpMessage={errors.provider?.message}
          disabled={isEditMode}
          {...register('provider')}
        />
      </FieldGroup>

      <FuelTypeGroup>
        <MenuGroup leftLabel={t('FUEL_MODAL_REGISTER-PROVIDER_SECTION_FUEL-OFFERED')}>
          <FuelSelectablePriceField
            id="fuel-provider-avgas-price"
            label={t('TOOLBAR_FUEL_ITEM_AVGAS')}
            checked={!!avgasProvider}
            onCheckedChange={(nextValue) => {
              setValue('avgasProvider', nextValue, { shouldDirty: true })
              if (!nextValue) setValue('avgasPrice', '', { shouldDirty: true })
            }}
            value={avgasPrice ?? ''}
            onValueChange={(nextValue) => {
              setValue('avgasPrice', formatCurrencyFromDigits(nextValue), { shouldDirty: true })
            }}
            onInputFocus={() => {
              if (avgasPrice) return
              setValue('avgasPrice', EMPTY_CURRENCY_VALUE, { shouldDirty: true })
            }}
            onInputBlur={() => {
              if (parseCurrencyInputToNumber(avgasPrice ?? '') !== null) return
              setValue('avgasPrice', '', { shouldDirty: true })
            }}
            placeholder={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_PRICE-INPUT_PLACEHOLDER')}
            error={!!errors.avgasPrice}
            helpMessage={errors.avgasPrice?.message}
            helpGutter={!!errors.avgasPrice}
          />
        </MenuGroup>

        <MenuGroup>
          <FuelSelectablePriceField
            id="fuel-provider-jet-price"
            label={t('TOOLBAR_FUEL_ITEM_JET-A')}
            checked={!!jetProvider}
            onCheckedChange={(nextValue) => {
              setValue('jetProvider', nextValue, { shouldDirty: true })
              if (!nextValue) setValue('jetPrice', '', { shouldDirty: true })
            }}
            value={jetPrice ?? ''}
            onValueChange={(nextValue) => {
              setValue('jetPrice', formatCurrencyFromDigits(nextValue), { shouldDirty: true })
            }}
            onInputFocus={() => {
              if (jetPrice) return
              setValue('jetPrice', EMPTY_CURRENCY_VALUE, { shouldDirty: true })
            }}
            onInputBlur={() => {
              if (parseCurrencyInputToNumber(jetPrice ?? '') !== null) return
              setValue('jetPrice', '', { shouldDirty: true })
            }}
            placeholder={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_PRICE-INPUT_PLACEHOLDER')}
            error={!!errors.jetPrice}
            helpMessage={errors.jetPrice?.message}
            helpGutter={!!errors.jetPrice}
          />
        </MenuGroup>
      </FuelTypeGroup>

      <ContactGroup>
        <ContactHeader>
          <ContactTitle>{t('FUEL_MODAL_REGISTER-PROVIDER_SECTION_CONTACT')}</ContactTitle>
          <AddContactButton
            type="button"
            onClick={() => {
              append({ phone: '', responsible: '' })
            }}
          >
            {t('FUEL_MODAL_REGISTER-PROVIDER_ADD-CONTACT')}
          </AddContactButton>
        </ContactHeader>
        <ContactList>
          {contactFields.map((contactField, index) => (
            <ContactRow key={contactField.id}>
              <TextField
                id={`fuel-provider-contact-phone-${index}`}
                size="md"
                placeholder={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_CONTACT_PHONE_PLACEHOLDER')}
                mask="(99) 99999-9999"
                inputMode="numeric"
                error={!!errors.contacts?.[index]?.phone}
                helpMessage={errors.contacts?.[index]?.phone?.message}
                {...register(`contacts.${index}.phone`)}
              />
              <TextField
                id={`fuel-provider-contact-responsible-${index}`}
                size="md"
                placeholder={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_CONTACT_RESPONSIBLE_PLACEHOLDER')}
                error={!!errors.contacts?.[index]?.responsible}
                helpMessage={errors.contacts?.[index]?.responsible?.message}
                {...register(`contacts.${index}.responsible`)}
              />
            </ContactRow>
          ))}
        </ContactList>
      </ContactGroup>

      <FieldGroup>
        <TextField
          id="fuel-provider-notes"
          label={t('FUEL_MODAL_REGISTER-PROVIDER_FIELD_NOTES_LABEL')}
          multiline
          rows={3}
          max={255}
          helpGutter={false}
          {...register('notes')}
        />
      </FieldGroup>

      <ActionsRow>
        <Button
          type="button"
          label={t('FUEL_MODAL_EDIT-PROVIDER_CANCEL')}
          variant="outline"
          color="secondary"
          size="md"
          onPress={onCancel}
        />
        <Button
          type="button"
          label={isEditMode ? t('FUEL_MODAL_EDIT-PROVIDER_SAVE') : t('TOOLBAR_FUEL_ITEM_REGISTER-SUPPLIER')}
          variant="filled"
          color="primary"
          size="md"
          onPress={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        />
      </ActionsRow>
    </FormContainer>
  )
}

export { RegisterProviderForm }
